// src/features/task/TaskPoliciesAdmin.tsx
// Admin: task policies bound to scope entities (project, team, department…),
// plus a resolver that shows the effective merged policy for an entity.
// Wraps List/Create/Update/Delete/Get/GetTaskPolicyByEntity/ResolveTaskContext.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTaskTaskPolicy, useTaskTaskType, useI18n } from '@react/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Input, Badge, Alert, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { PipelinePicker } from '@/components/PipelinePicker';
import { useScope } from '@/hooks/useScope';
import { taskPriorityOptions } from '@/features/task/logic';
import { SCOPE_ENTITY_TYPES, diffFields, errMsg, makeEntity } from '@/features/task/panelHelpers';
import { ChipMultiSelect } from '@/features/task/ChipMultiSelect';
import type { TaskPolicy } from '@core/types/task';

const EDITABLE_FIELDS: (keyof TaskPolicy)[] = [
  'name', 'scope_entity', 'scope_entity_type', 'default_priority', 'allowed_task_type_ids',
  'enable_time_tracking', 'enable_checklists', 'enable_dependencies', 'enable_custom_fields', 'is_active',
  'default_pipeline', 'required_pipeline',
];

interface EditFormState {
  name: string;
  scope_type: string;
  scope_id: string;
  default_priority: string;
  allowed_task_type_ids: string[];
  enable_time_tracking: boolean;
  enable_checklists: boolean;
  enable_dependencies: boolean;
  enable_custom_fields: boolean;
  is_active: boolean;
  default_pipeline_id: string;
  required_pipeline_id: string;
}

export const TaskPoliciesAdmin = observer(() => {
  const store = useTaskTaskPolicy();
  const taskTypeStore = useTaskTaskType();
  const { t } = useI18n();
  const scope = useScope();

  const [name, setName] = useState('');
  const [scopeType, setScopeType] = useState<string>(SCOPE_ENTITY_TYPES[0]);
  const [scopeId, setScopeId] = useState('');
  const [priority, setPriority] = useState('');
  const [timeTracking, setTimeTracking] = useState(true);
  const [dependencies, setDependencies] = useState(true);
  const [defaultPipelineId, setDefaultPipelineId] = useState('');
  const [requiredPipelineId, setRequiredPipelineId] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const [lookupType, setLookupType] = useState<string>(SCOPE_ENTITY_TYPES[0]);
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<string | null>(null);
  const [resolveResult, setResolveResult] = useState<{ name: string; types: number } | null>(null);
  const [toolError, setToolError] = useState<string | null>(null);
  const [toolBusy, setToolBusy] = useState(false);

  useEffect(() => {
    store.ListTaskPolicies();
    taskTypeStore.ListTaskTypes({ pagination: { limit: 100, cursor: '', direction: 'forward' }, sort: [] } as any);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const typeOptions = taskTypeStore.items.map((tt: any) => ({ value: tt.entity?.id || '', label: tt.display_name }));

  const create = async () => {
    if (!name.trim()) return;
    const scopeEntity = scopeId.trim() ? makeEntity(scopeId.trim(), scopeType, scope) : makeEntity(scope.tenantId, 'tenant', scope);
    try {
      await store.CreateTaskPolicy({
        entity: makeEntity('', 'task_policy', scope),
        name: name.trim(),
        scope_entity: scopeEntity,
        scope_entity_type: scopeType,
        default_priority: priority ? (priority as any) : undefined,
        enable_time_tracking: timeTracking,
        enable_dependencies: dependencies,
        default_pipeline: defaultPipelineId ? { id: defaultPipelineId } : undefined,
        required_pipeline: requiredPipelineId ? { id: requiredPipelineId } : undefined,
      } as any);
      setName(''); setScopeId(''); setPriority(''); setDefaultPipelineId(''); setRequiredPipelineId('');
    } catch { /* store.error */ }
  };

  const startEdit = async (id: string) => {
    setEditingId(id);
    try {
      const p = await store.GetTaskPolicy(id);
      setEditForm({
        name: p?.name || '',
        scope_type: p?.scope_entity_type || SCOPE_ENTITY_TYPES[0],
        scope_id: p?.scope_entity?.id || '',
        default_priority: (p?.default_priority as string) || '',
        allowed_task_type_ids: p?.allowed_task_type_ids ? [...p.allowed_task_type_ids] : [],
        enable_time_tracking: p?.enable_time_tracking ?? false,
        enable_checklists: p?.enable_checklists ?? false,
        enable_dependencies: p?.enable_dependencies ?? false,
        enable_custom_fields: p?.enable_custom_fields ?? false,
        is_active: p?.is_active ?? true,
        default_pipeline_id: p?.default_pipeline?.id || '',
        required_pipeline_id: p?.required_pipeline?.id || '',
      });
    } catch {
      setEditForm(null);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const toggleAllowedType = (typeId: string) => {
    if (!editForm) return;
    const set = new Set(editForm.allowed_task_type_ids);
    if (set.has(typeId)) set.delete(typeId); else set.add(typeId);
    setEditForm({ ...editForm, allowed_task_type_ids: Array.from(set) });
  };

  const saveEdit = async (id: string) => {
    if (!editForm) return;
    const current = store.items.find((p) => p.entity?.id === id) || null;
    const updated: TaskPolicy = {
      ...(current || ({} as TaskPolicy)),
      name: editForm.name.trim(),
      scope_entity: editForm.scope_id.trim() ? makeEntity(editForm.scope_id.trim(), editForm.scope_type, scope) : current?.scope_entity,
      scope_entity_type: editForm.scope_type,
      default_priority: (editForm.default_priority || undefined) as TaskPolicy['default_priority'],
      allowed_task_type_ids: editForm.allowed_task_type_ids,
      enable_time_tracking: editForm.enable_time_tracking,
      enable_checklists: editForm.enable_checklists,
      enable_dependencies: editForm.enable_dependencies,
      enable_custom_fields: editForm.enable_custom_fields,
      is_active: editForm.is_active,
      default_pipeline: editForm.default_pipeline_id ? makeEntity(editForm.default_pipeline_id, 'pipeline', scope) : undefined,
      required_pipeline: editForm.required_pipeline_id ? makeEntity(editForm.required_pipeline_id, 'pipeline', scope) : undefined,
    };
    const paths = diffFields(current, updated, EDITABLE_FIELDS);
    if (paths.length === 0) { cancelEdit(); return; }
    try {
      await store.UpdateTaskPolicy(id, { policy: updated, field_mask: paths } as any);
      cancelEdit();
    } catch { /* store.error */ }
  };

  const remove = async (id: string) => {
    try { await store.DeleteTaskPolicy(id); setConfirmId(null); } catch { /* store.error */ }
  };

  const lookupByEntity = async () => {
    if (!lookupId.trim()) return;
    setToolBusy(true); setToolError(null); setLookupResult(null);
    try {
      const p = await store.GetTaskPolicyByEntity(lookupType, lookupId.trim());
      setLookupResult(p?.name || t('task.policies.noneAttached'));
    } catch (e) {
      setToolError(errMsg(e, t('task.policies.lookupError')));
    } finally {
      setToolBusy(false);
    }
  };

  const resolve = async () => {
    if (!lookupId.trim()) return;
    setToolBusy(true); setToolError(null); setResolveResult(null);
    try {
      const entity = makeEntity(lookupId.trim(), lookupType, scope);
      const res: any = await store.ResolveTaskContext({ scope_entity: entity, scope_chain: [entity] } as any);
      setResolveResult({
        name: res?.effective_policy?.name || t('task.policies.noEffective'),
        types: (res?.allowed_types || []).length,
      });
    } catch (e) {
      setToolError(errMsg(e, t('task.policies.resolveError')));
    } finally {
      setToolBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.policies.title')}</CardTitle>
        <CardDescription>{t('task.policies.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

        {store.isLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}</div>
        ) : store.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.policies.empty')}</p>
        ) : (
          <div className="space-y-2">
            {store.items.map((p) => {
              const id = p.entity?.id || '';
              const isEditing = editingId === id;
              return (
                <div key={id} className="rounded-md border border-border">
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{p.name}</span>
                      {p.scope_entity_type && <Badge variant="secondary" size="sm">{p.scope_entity_type}</Badge>}
                      {!p.is_active && <Badge variant="secondary" size="sm">{t('common.inactive')}</Badge>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isEditing ? (
                        <>
                          <Button size="sm" isLoading={store.isSaving} onClick={() => saveEdit(id)}>{t('common.save')}</Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit}>{t('common.cancel')}</Button>
                        </>
                      ) : confirmId === id ? (
                        <>
                          <Button size="sm" variant="danger" isLoading={store.isDeleting} onClick={() => remove(id)}>{t('common.confirm')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>{t('common.cancel')}</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(id)}>{t('common.edit')}</Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmId(id)}>{t('common.delete')}</Button>
                        </>
                      )}
                    </div>
                  </div>

                  {isEditing && editForm && (
                    <div className="space-y-3 border-t border-border px-3 py-3">
                      <Input label={t('task.policies.name')} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.scopeType')}</label>
                          <Select value={editForm.scope_type} onChange={(e) => setEditForm({ ...editForm, scope_type: e.target.value })}>
                            {SCOPE_ENTITY_TYPES.map((st) => <option key={st} value={st}>{st}</option>)}
                          </Select>
                        </div>
                        <Input
                          label={t('task.policies.scopeId')}
                          value={editForm.scope_id}
                          onChange={(e) => setEditForm({ ...editForm, scope_id: e.target.value })}
                          placeholder={t('task.policies.scopeIdPlaceholder')}
                          helperText={t('task.policies.scopeIdHint')}
                        />
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.defaultPriority')}</label>
                          <Select value={editForm.default_priority} onChange={(e) => setEditForm({ ...editForm, default_priority: e.target.value })}>
                            <option value="">{t('task.allPriorities')}</option>
                            {taskPriorityOptions.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <ToggleSwitch label={t('task.policies.timeTracking')} checked={editForm.enable_time_tracking} onChange={(v) => setEditForm({ ...editForm, enable_time_tracking: v })} />
                        <ToggleSwitch label={t('task.checklists')} checked={editForm.enable_checklists} onChange={(v) => setEditForm({ ...editForm, enable_checklists: v })} />
                        <ToggleSwitch label={t('task.policies.dependencies')} checked={editForm.enable_dependencies} onChange={(v) => setEditForm({ ...editForm, enable_dependencies: v })} />
                        <ToggleSwitch label={t('task.customFields')} checked={editForm.enable_custom_fields} onChange={(v) => setEditForm({ ...editForm, enable_custom_fields: v })} />
                        <ToggleSwitch label={t('common.active')} checked={editForm.is_active} onChange={(v) => setEditForm({ ...editForm, is_active: v })} />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-border">
                        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('task.policies.allowedTaskTypes')}</span>
                        <ChipMultiSelect
                          options={typeOptions}
                          selected={editForm.allowed_task_type_ids}
                          onToggle={toggleAllowedType}
                          emptyLabel={t('task.policies.noTaskTypes')}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.defaultPipeline')}</label>
                          <PipelinePicker value={editForm.default_pipeline_id} onChange={(id) => setEditForm({ ...editForm, default_pipeline_id: id })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.requiredPipeline')}</label>
                          <PipelinePicker value={editForm.required_pipeline_id} onChange={(id) => setEditForm({ ...editForm, required_pipeline_id: id })} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground">{t('task.policies.addNew')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input label={t('task.policies.name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('task.policies.namePlaceholder')} />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.scopeType')}</label>
              <Select value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
                {SCOPE_ENTITY_TYPES.map((st) => <option key={st} value={st}>{st}</option>)}
              </Select>
            </div>
            <Input
              label={t('task.policies.scopeId')}
              value={scopeId}
              onChange={(e) => setScopeId(e.target.value)}
              placeholder={t('task.policies.scopeIdPlaceholder')}
              helperText={t('task.policies.scopeIdHint')}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.defaultPriority')}</label>
              <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                <option value="">{t('task.allPriorities')}</option>
                {taskPriorityOptions.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </Select>
            </div>
            <ToggleSwitch label={t('task.policies.timeTracking')} checked={timeTracking} onChange={setTimeTracking} className="mt-6" />
            <ToggleSwitch label={t('task.policies.dependencies')} checked={dependencies} onChange={setDependencies} className="mt-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.defaultPipeline')}</label>
              <PipelinePicker value={defaultPipelineId} onChange={setDefaultPipelineId} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.requiredPipeline')}</label>
              <PipelinePicker value={requiredPipelineId} onChange={setRequiredPipelineId} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={create} disabled={!name.trim()} isLoading={store.isSaving}>{t('task.policies.add')}</Button>
          </div>
        </div>

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground">{t('task.policies.resolverTitle')}</h4>
          <p className="text-xs text-muted-foreground">{t('task.policies.resolverHint')}</p>
          {toolError && <Alert variant="error">{toolError}</Alert>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.policies.scopeType')}</label>
              <Select value={lookupType} onChange={(e) => setLookupType(e.target.value)}>
                {SCOPE_ENTITY_TYPES.map((st) => <option key={st} value={st}>{st}</option>)}
              </Select>
            </div>
            <Input label={t('task.policies.scopeId')} value={lookupId} onChange={(e) => setLookupId(e.target.value)} placeholder={t('task.policies.scopeIdPlaceholder')} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={lookupByEntity} isLoading={toolBusy} disabled={!lookupId.trim()}>{t('task.policies.lookup')}</Button>
            <Button size="sm" variant="secondary" onClick={resolve} isLoading={toolBusy} disabled={!lookupId.trim()}>{t('task.policies.resolve')}</Button>
          </div>
          {lookupResult && <p className="text-sm text-foreground">{t('task.policies.attachedPolicy')}: <span className="font-medium">{lookupResult}</span></p>}
          {resolveResult && (
            <p className="text-sm text-foreground">
              {t('task.policies.effectivePolicy')}: <span className="font-medium">{resolveResult.name}</span>
              <span className="text-muted-foreground"> · {t('task.policies.allowedTypes', { count: resolveResult.types })}</span>
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default TaskPoliciesAdmin;
